import { useEffect, useState } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { IRemoveGuestUsersActionResultProps } from "./remove-guest-users-action-result.props";
import { IActionResult } from "@interfaces/actions/actions-common.interfaces";
import { IRemoveGuestUsersResult, IRemovedGuestUser, IUserWithWorkspaces, IWorkspaceWithUsers } from "@interfaces/actions/guest-user-actions.interfaces";
import RemoveGuestUsersActionResultCard from "./remove-guest-users-action-result-card";
import ActionResultNoResultCard from "components/actions-common/action-result-no-result-card/action-result-no-result-card";
import ServiceMessagesCard from "components/common/service-messages-card/service-messages-card";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";

const layoutStyles = makeStyles({
    mainWrapper: {
        ...shorthands.margin('1em', 0, 0, 0)
    }
});

export default function RemoveGuestUsersActionResult(props: IRemoveGuestUsersActionResultProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();

    const [actionResult, setActionResult] = useState<IActionResult<IRemoveGuestUsersResult>>();
    const [users, setUsers] = useState<Array<IUserWithWorkspaces>>([]);
    const [workspaces, setWorkspaces] = useState<Array<IWorkspaceWithUsers>>([]);
    const [view, setView] = useState<string>("user");

    useEffect(() => {
        if (props.getResult) {
            props.getResult(`/api/actions/remove-guest-users/${props.requestId}`).then(
                (data: IActionResult<IRemoveGuestUsersResult>) => {
                    console.log(`RemoveGuestUsersActionResult -> useEffect -> data -> `, data);

                    setActionResult(data);
                },
                (error: any) => {
                    console.log(`RemoveGuestUsersActionResult -> useEffect -> error -> `, error);
                });
        }
    }, []);

    useEffect(() => {
        console.log(`RemoveGuestUsersActionResult -> useEffect -> result.removed -> `, actionResult?.result?.removed);
        const users: Array<IUserWithWorkspaces> = [];
        const workspaces: Array<IWorkspaceWithUsers> = [];

        if (actionResult?.result?.removed) {
            actionResult.result.removed.forEach((removedUser: IRemovedGuestUser) => {
                console.log(`removedUser -> `, removedUser.upn);
                const userMatch = users.filter(u => u.upn === removedUser.upn);
                if (userMatch.length === 0) {
                    users.push({
                        upn: removedUser.upn,
                        objectId: removedUser.aadObjectId || "",
                        displayName: removedUser.displayName,
                        email: removedUser.email,
                        workspaces: [
                            {
                                siteUrl: removedUser.workspaceUrl,
                                groupId: "",
                                title: removedUser.workspaceName,
                                template: removedUser.workspaceTemplate,
                                workspaceType: removedUser.workspaceType
                            }
                        ]
                    });
                }
                else {
                    // Update the user to add the workspace...
                    userMatch[0].workspaces.push({
                        siteUrl: removedUser.workspaceUrl,
                        groupId: "",
                        title: removedUser.workspaceName,
                        template: removedUser.workspaceTemplate,
                        workspaceType: removedUser.workspaceType
                    });
                }
                const workspaceMatch = workspaces.filter(w => w.siteUrl === removedUser.workspaceUrl);
                if (workspaceMatch.length === 0) {
                    workspaces.push({
                        siteUrl: removedUser.workspaceUrl,
                        groupId: "",
                        title: removedUser.workspaceName,
                        template: removedUser.workspaceTemplate,
                        workspaceType: removedUser.workspaceType,
                        users: [
                            {
                                upn: removedUser.upn,
                                objectId: removedUser.aadObjectId || "",
                                displayName: removedUser.displayName,
                                email: removedUser.email
                            }
                        ]
                    });
                }
                else {
                    // Update the workspace to add the user...
                    workspaceMatch[0].users.push({
                        upn: removedUser.upn,
                        objectId: removedUser.aadObjectId || "",
                        displayName: removedUser.displayName,
                        email: removedUser.email
                    });
                }
            });

            if (actionResult.result.removed.length === 0) {
                console.log(`length is zero...`);
            }
        }
        else {
            // Clear...
        }

        setUsers(users);
        setWorkspaces(workspaces);

        console.log(`processed -> `, users, workspaces);

        if (users.length === 1 && workspaces.length >= 1) {
            // user view
            setView("user");
        }
        else if (workspaces.length === 1 && users.length >= 1) {
            // workspace view
            setView("workspace");
        }
        else if (users.length >= 1 && workspaces.length >= 1) {
            // multi view
            setView("multiple");
        }
        else if (users.length === 0 && workspaces.length === 0) {
            setView("none");
        }

    }, [actionResult]);

    const [showMessages, setShowMessages] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);

    return (
        <div className={styles.mainWrapper}>
            {!showErrors && !showMessages &&
                <>
                    {actionResult && view === "user" && users[0] &&
                        <RemoveGuestUsersActionResultCard
                            user={users[0]}
                            errorCount={actionResult.errorCount}
                            messages={actionResult.messages}
                            onMessagesClick={() => { console.log(`onMessagesClick ->`); setShowMessages(true); }}
                            onErrorsClick={() => { console.log(`onErrorsClick ->`); setShowErrors(true); }}
                        />}
                    {actionResult && view === "workspace" && workspaces[0] &&
                        <RemoveGuestUsersActionResultCard
                            workspace={workspaces[0]}
                            errorCount={actionResult.errorCount}
                            messages={actionResult.messages}
                            onMessagesClick={() => { console.log(`onMessagesClick ->`); setShowMessages(true); }}
                            onErrorsClick={() => { console.log(`onErrorsClick ->`); setShowErrors(true); }}
                        />}
                    {actionResult && view === "multiple" &&
                        <div>
                            {users.map((user, idx) => (
                                <RemoveGuestUsersActionResultCard
                                    key={idx}
                                    user={user}
                                    errorCount={actionResult.errorCount}
                                    messages={actionResult.messages}
                                    onMessagesClick={() => { console.log(`onMessagesClick ->`); setShowMessages(true); }}
                                    onErrorsClick={() => { console.log(`onErrorsClick ->`); setShowErrors(true); }}
                                />
                            ))}
                        </div>}
                    {actionResult && view === "none" &&
                        <ActionResultNoResultCard
                            message={t('ppaRemoveGuestUsersActionNoResultsMessage')}
                            errorCount={actionResult.errorCount}
                            messages={actionResult.messages}
                            onMessagesClick={() => { console.log(`onMessagesClick ->`); setShowMessages(true); }}
                            onErrorsClick={() => { console.log(`onErrorsClick ->`); setShowErrors(true); }} />}
                </>}
            {actionResult && showMessages &&
                <ServiceMessagesCard
                    title={t('messages')}
                    messages={actionResult.messages}
                    onClose={() => setShowMessages(false)}
                />}
            {actionResult && showErrors &&
                <ServiceMessagesCard
                    title={t('errors')}
                    messages={actionResult.messages.filter(m => m.type === 3 || m.type === 7)}
                    onClose={() => setShowErrors(false)}
                />}
        </div>
    );
}